import React, { useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { API_BASE } from "../lib/api";

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(1) + " MB";
}

function iconFor(mime) {
  if (!mime) return "📄";
  if (mime.startsWith("image/")) return "🖼️";
  if (mime.startsWith("video/")) return "🎬";
  if (mime.startsWith("audio/")) return "🎵";
  if (mime.includes("pdf")) return "📕";
  if (mime.includes("zip") || mime.includes("compressed")) return "🗜️";
  if (mime.includes("sheet") || mime.includes("excel") || mime.includes("csv")) return "📊";
  if (mime.includes("word") || mime.includes("document")) return "📝";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "📽️";
  return "📄";
}

export default function FilePicker({ taskId, files, onChange }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState({}); // tempId -> progress 0-100

  async function uploadOne(file) {
    const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
    setUploading((s) => ({ ...s, [tempId]: 0 }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (taskId) fd.append("taskId", taskId);
      const token = localStorage.getItem("token");
      const xhr = new XMLHttpRequest();
      const promise = new Promise((resolve, reject) => {
        xhr.open("POST", `${API_BASE}/files/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploading((s) => ({ ...s, [tempId]: Math.round((e.loaded / e.total) * 100) }));
          }
        };
        xhr.onload = () => {
          let body = {};
          try { body = JSON.parse(xhr.responseText); } catch (e) {}
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else reject(new Error(body.message || `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(fd);
      });
      const r = await promise;
      onChange && onChange((prev) => [r.file, ...(prev || [])]);
      setUploading((s) => { const c = { ...s }; delete c[tempId]; return c; });
    } catch (e) {
      toast({ title: e.message || "Upload failed", status: "error" });
      setUploading((s) => { const c = { ...s }; delete c[tempId]; return c; });
    }
  }

  async function uploadMany(fileList) {
    for (const f of Array.from(fileList)) await uploadOne(f);
  }

  async function deleteFile(id) {
    if (!window.confirm("Delete this file?")) return;
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_BASE}/files/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Delete failed");
      onChange && onChange((prev) => (prev || []).filter((f) => f._id !== id));
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  function downloadUrl(file) {
    return `${API_BASE}/files/${file._id}/download`;
  }
  function previewUrl(file) {
    return `${API_BASE}/files/${file._id}/preview`;
  }

  function authedHref(url) {
    // We need to attach the bearer token; simplest is to fetch as blob and open.
    return async (e) => {
      e.preventDefault();
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        if (url.endsWith("/download")) {
          // try to use original filename from cd header
          const cd = res.headers.get("Content-Disposition") || "";
          const m = cd.match(/filename="([^"]+)"/);
          a.download = m ? decodeURIComponent(m[1]) : "download";
        } else {
          a.target = "_blank";
        }
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 60_000);
      } catch (err) { toast({ title: err.message, status: "error" }); }
    };
  }

  const inProgressItems = Object.entries(uploading);

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files && e.dataTransfer.files.length) uploadMany(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={[
          "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition",
          dragOver ? "border-blue-500 bg-blue-50" : "border-ink-200 hover:border-ink-300 hover:bg-ink-50",
        ].join(" ")}
      >
        <div className="text-2xl mb-1">⬆️</div>
        <div className="text-sm text-ink-600">
          {dragOver ? "Drop to upload" : "Click or drag files here"}
        </div>
        <div className="text-[11px] text-ink-400 mt-0.5">Max 25 MB per file</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) uploadMany(e.target.files); e.target.value = ""; }}
        />
      </div>

      {inProgressItems.length > 0 && (
        <div className="mt-3 space-y-1">
          {inProgressItems.map(([id, pct]) => (
            <div key={id} className="text-xs">
              <div className="flex justify-between mb-0.5"><span className="text-ink-500">Uploading…</span><span className="text-ink-500">{pct}%</span></div>
              <div className="h-1 bg-ink-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {(files || []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {(files || []).map((f) => {
            const isImage = (f.mime || "").startsWith("image/");
            return (
              <li key={f._id} className="flex items-center gap-3 bg-ink-50 hover:bg-white border border-transparent hover:border-ink-200 rounded-md p-2 transition">
                <div className="text-xl flex-shrink-0">{iconFor(f.mime)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink-700 font-medium truncate">{f.name}</div>
                  <div className="text-[11px] text-ink-400">
                    {fmtBytes(f.size)} · {f.uploadedBy?.username || "—"} · {new Date(f.createdAt).toLocaleString()}
                    {f.provider === "gdrive" && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-semibold">Drive</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  {isImage && (
                    <a href={previewUrl(f)} onClick={authedHref(previewUrl(f))} className="px-2 py-1 rounded text-blue-600 hover:bg-blue-50">Preview</a>
                  )}
                  <a href={downloadUrl(f)} onClick={authedHref(downloadUrl(f))} className="px-2 py-1 rounded text-blue-600 hover:bg-blue-50">Download</a>
                  <button onClick={() => deleteFile(f._id)} className="px-2 py-1 rounded text-ink-400 hover:text-red-500">✕</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
